import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { GuiMsjService } from 'src/app/services/shared/gui-msj.service';
import { Catalogo } from '../../../../../interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { CatalogoService } from '../../../../../services/shared/catalogo.service';
import { CatalogoDinamico } from '../../../../../interfaces/interface-bakend/shared/catalogo/catalogo-dinamico.interface';
import { EntidadesKeys } from 'src/app/enum/entidades-keys.enum';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-rubro-modificacion',
    templateUrl: './rubro-modificacion.component.html',
    styleUrls: ['./rubro-modificacion.component.scss'],
})
export class RubroModificacionComponent implements OnInit {
    form: FormGroup;
    catalogo: Catalogo;
    especialidades: CatalogoDinamico[] = [];
    especialidadesOrdenadas: CatalogoDinamico[] = [];
    especialidadesSeleccionadas: Set<string> = new Set();
    rubros: Catalogo[] = [];
    
    constructor(
        private router: Router,
        private catalogoService: CatalogoService,
        public guiMsjService: GuiMsjService
    ) {}

    async ngOnInit(): Promise<void> {
        this.catalogo = this.catalogoService.getConceptoSeleccionado();
        if (!this.catalogo) {
            await this.router.navigateByUrl('/rubros-listado');
            return;
        }
        this.createForm();
        await this.obtenerDatosIniciales();
    }

    get rubroNoValido() {
        return this.form.get('rubro').invalid && this.form.get('rubro').touched;
    }

    async obtenerDatosIniciales() {
        try {
            this.rubros = await this.catalogoService.findDescendantsByKey(EntidadesKeys.RUBROS);
            const allEspecialidades = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES) as CatalogoDinamico[];
            
            // Filtrar solo las especialidades que tienen este rubro asignado
            this.especialidades = allEspecialidades;
            this.ordenarEspecialidades();
            this.marcarEspecialidadesAsignadas();
        } catch (error) {
            console.error('Error al obtener datos:', error);
        }
    }

    getNombreRubro(especialidad: CatalogoDinamico): string {
      if (!especialidad.properties || !especialidad.properties.rubro) {
        return '';
      }
      
      const rubro = especialidad.properties.rubro;
      
      // Si es string (ID)
      if (typeof rubro === 'string') {
        return this.rubroMap.get(rubro) || '';
      }
      
      // Si es un objeto, hacer cast a any y verificar _id
      if (typeof rubro === 'object' && rubro !== null) {
        const rubroObj = rubro as any;
        if (rubroObj._id) {
          return this.rubroMap.get(rubroObj._id) || '';
        }
      }
      
      return '';
    }

    // Crear mapa de rubros para búsqueda rápida
    get rubroMap(): Map<string, string> {
      const mapa = new Map<string, string>();
      this.rubros.forEach(rubro => {
        mapa.set(rubro._id!, rubro.concepto!);
      });
      return mapa;
    }

    getRubroId(especialidad: CatalogoDinamico): string {
      if (!especialidad.properties || !especialidad.properties.rubro) return '';
      
      const rubro = especialidad.properties.rubro;
      
      if (typeof rubro === 'string') {
        return rubro;
      }
      
      // Si es un objeto, hacer cast a any y obtener _id
      if (typeof rubro === 'object' && rubro !== null) {
        const rubroObj = rubro as any;
        return rubroObj._id || '';
      }
      
      return '';
    }

    esEspecialidadDeEsteRubro(especialidad: CatalogoDinamico): boolean {
      const rubroId = this.getRubroId(especialidad);
      return rubroId === this.catalogo._id;
    }

    ordenarEspecialidades() {
        this.especialidadesOrdenadas = [...this.especialidades].sort((a, b) => {
            const rubroA = this.getNombreRubro(a);
            const rubroB = this.getNombreRubro(b);
            
            if ((rubroA && rubroB) || (!rubroA && !rubroB)) {
                return a.concepto!.localeCompare(b.concepto!);
            }
            return rubroA ? -1 : 1;
        });
    }

    marcarEspecialidadesAsignadas() {
        this.especialidades.forEach(especialidad => {
            if (this.esEspecialidadDeEsteRubro(especialidad)) {
                this.especialidadesSeleccionadas.add(especialidad._id!);
            }
        });
    }

    onEspecialidadSeleccionada(event: any, especialidadId: string) {
        event.checked 
            ? this.especialidadesSeleccionadas.add(especialidadId)
            : this.especialidadesSeleccionadas.delete(especialidadId);
    }

    createForm() {
        this.form = new FormGroup({
            rubro: new FormControl(this.catalogo?.concepto, [
                Validators.required,
                Validators.min(3),
            ]),
            activo: new FormControl(this.catalogo?.logico_1, [
                Validators.required,
            ]),
        });
    }

    async setDataForma(): Promise<Catalogo> {
        const data: Catalogo = {
            key: null,
            concepto: this.form.value.rubro,
            logico_1: this.form.value.activo,
            parent: this.catalogo.parent,
            properties: {},
        };

        return data;
    }

    async asignarRubroAEspecialidades(rubroId: string, especialidadesIds: string[], esRemover: boolean = false) {
        if (especialidadesIds.length === 0) return;
        
        const promesas = especialidadesIds.map(async especialidadId => {
            const especialidad = this.especialidades.find(e => e._id === especialidadId);
            if (!especialidad) return;
            
            // Crear una copia de properties existentes o un objeto vacío
            const existingProperties = especialidad.properties || {};
            
            const datosActualizados: CatalogoDinamico = {
                ...especialidad,
                properties: {
                    ...existingProperties,
                    rubro: esRemover ? null : rubroId  // Asignamos null para remover o el ID string
                }
            };
            
            return this.catalogoService.put(especialidadId, datosActualizados)
                .catch(err => console.error(`Error al actualizar especialidad ${especialidadId}:`, err));
        });
        
        await Promise.all(promesas);
    }

    async updateRubro() {
        await this.guiMsjService.msjFormSubmit('Guardando');
        
        try {
            // 1. Actualizar el rubro
            await this.catalogoService.put(this.catalogo._id!, await this.setDataForma());
            
            // 2. Obtener todas las especialidades que actualmente tienen este rubro
            const todasEspecialidades = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES) as CatalogoDinamico[];
            const especialidadesActuales = todasEspecialidades.filter(e => this.esEspecialidadDeEsteRubro(e));
            const idsActuales = especialidadesActuales.map(e => e._id!);
            
            const idsSeleccionadas = Array.from(this.especialidadesSeleccionadas);
            
            const idsARemover = idsActuales.filter(id => !idsSeleccionadas.includes(id));
            const idsAAsignar = idsSeleccionadas.filter(id => !idsActuales.includes(id));
            
            console.log(`Rubro ${this.catalogo.concepto}: Remover ${idsARemover.length}, Asignar ${idsAAsignar.length}`);
            
            // 3. Actualizar asignaciones
            if (idsARemover.length > 0) {
                await this.asignarRubroAEspecialidades(this.catalogo._id!, idsARemover, true);
            }
            
            if (idsAAsignar.length > 0) {
                await this.asignarRubroAEspecialidades(this.catalogo._id!, idsAAsignar, false);
            }
            
            Swal.fire({
                icon: 'success',
                title: 'Rubro modificado satisfactoriamente',
                showConfirmButton: false,
                timer: 1100,
            });
            this.router.navigateByUrl('/rubros-listado');
            
        } catch (error) {
            console.error('Error en updateRubro:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al modificar rubro',
            });
        }
    }
}